// Iter 82.3 / Phase E1.3 — Utilization Report.
// Per-employee leave consumption % with summary widgets, table, and
// CSV + XLSX exports honouring current filters.
import { useCallback, useEffect, useState } from "react";
import { DownloadSimple, FunnelSimple, FileXls } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

export default function LeaveUtilizationReport() {
    const [fys, setFys] = useState([]);
    const [hrMeta, setHrMeta] = useState({ departments: [] });
    const [policies, setPolicies] = useState([]);
    const [filters, setFilters] = useState({
        fiscal_year: "",
        department: "",
        leave_type_code: "",
    });
    const [data, setData] = useState({ items: [], summary: {}, fiscal_year: "" });
    const [loading, setLoading] = useState(true);

    const loadMeta = useCallback(async () => {
        try {
            const [fy, hr, pl] = await Promise.all([
                api.get("/leave/fiscal-year/list"),
                api.get("/hr/employee/meta"),
                api.get("/leave/policy/list"),
            ]);
            setFys(fy.data.items || []);
            setHrMeta(hr.data);
            setPolicies(pl.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load meta"));
        }
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: payload } = await api.get("/leave/report/utilization", { params });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load report"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);
    useEffect(() => { reload(); }, [reload]);

    const download = async (ext) => {
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: blob } = await api.get(`/leave/report/utilization/export.${ext}`, {
                params, responseType: "blob",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `leave-utilization-${data.fiscal_year}-${new Date().toISOString().slice(0, 10)}.${ext}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, `${ext.toUpperCase()} export failed`));
        }
    };

    const summary = data.summary || {};

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="leave-utilization-page">
            <PageHeader
                eyebrow="Leave Reports"
                title="Utilization Report"
                description={`Per-employee leave consumption analysis for FY ${data.fiscal_year || ""}`}
                actions={(
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="util-export-csv">
                            <DownloadSimple size={14} className="mr-1" /> CSV
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="util-export-xlsx">
                            <FileXls size={14} className="mr-1" /> Excel
                        </Button>
                    </div>
                )}
            />

            <section className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="util-summary">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Employees in Report</div>
                    <div className="text-3xl font-bold text-slate-900 mt-1">{summary.total_employees || 0}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Avg Utilization %</div>
                    <div className="text-3xl font-bold text-amber-900 mt-1">{(summary.avg_utilization_pct || 0).toFixed(1)}%</div>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-rose-800 font-semibold">Top Consumer</div>
                    <div className="text-sm font-bold text-rose-900 mt-1">
                        {(summary.top_consumers && summary.top_consumers[0]?.employee_name) || "—"}
                    </div>
                    <div className="text-xs text-rose-700">
                        {(summary.top_consumers && summary.top_consumers[0]) ? `${summary.top_consumers[0].consumed} days (${summary.top_consumers[0].leave_type_code})` : ""}
                    </div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Highest Available Balance</div>
                    <div className="text-sm font-bold text-emerald-900 mt-1">
                        {(summary.high_balance && summary.high_balance[0]?.employee_name) || "—"}
                    </div>
                    <div className="text-xs text-emerald-700">
                        {(summary.high_balance && summary.high_balance[0]) ? `${summary.high_balance[0].current_balance.toFixed(1)} days (${summary.high_balance[0].leave_type_code})` : ""}
                    </div>
                </div>
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="util-filters">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
                    <FunnelSimple size={14} /> Filters
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <label className="text-xs text-slate-600">Fiscal Year</label>
                        <select data-testid="util-filter-fy" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.fiscal_year} onChange={(e) => setFilters({ ...filters, fiscal_year: e.target.value })}>
                            <option value="">Current FY</option>
                            {fys.map((fy) => (<option key={fy.id} value={fy.code}>{fy.code}{fy.is_current ? " (current)" : ""}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Department</label>
                        <select data-testid="util-filter-dept" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })}>
                            <option value="">All departments</option>
                            {(hrMeta.departments || []).map((d) => (<option key={d.id || d.name} value={d.name}>{d.name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Leave Type</label>
                        <select data-testid="util-filter-type" className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.leave_type_code} onChange={(e) => setFilters({ ...filters, leave_type_code: e.target.value })}>
                            <option value="">All</option>
                            {policies.map((p) => (<option key={p.code} value={p.code}>{p.code}</option>))}
                        </select>
                    </div>
                </div>
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="util-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Dept</th>
                                <th className="text-left px-3 py-2 font-medium">Type</th>
                                <th className="text-right px-3 py-2 font-medium">Entitled</th>
                                <th className="text-right px-3 py-2 font-medium">Consumed</th>
                                <th className="text-right px-3 py-2 font-medium">Current</th>
                                <th className="text-right px-3 py-2 font-medium">Util %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={7} className="text-center py-10 text-slate-400">Loading…</td></tr>}
                            {!loading && data.items.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-10 text-slate-400">No utilization data for current filters.</td></tr>
                            )}
                            {!loading && data.items.map((r) => (
                                <tr key={`${r.employee_id}-${r.leave_type_code}`} className="border-t border-slate-100" data-testid={`util-row-${r.employee_id}-${r.leave_type_code}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-slate-900">{r.employee_name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{r.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600">{r.department || "—"}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.leave_type_code}</td>
                                    <td className="px-3 py-2 text-right">{r.entitled.toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right text-rose-700">{r.consumed.toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{r.current_balance.toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <Badge className={`text-xs ${r.utilization_pct > 75 ? "bg-rose-100 text-rose-800" : r.utilization_pct > 50 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                                            {r.utilization_pct.toFixed(0)}%
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
